import { DefaultButton, PersonaInitialsColor, Spinner, SpinnerSize, Text } from '@fluentui/react';
import { getter } from '@progress/kendo-data-query';
import { getSelectedState, Grid, GridColumn as Column, GridSelectionChangeEvent, GridCellProps } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import AgingProgress from 'app/components/agingProgress/AgingProgress';
import Avatar from 'app/components/avatar/Avatar';
import { formatCurrency, formatRequestType, requestAggregateSubmit } from 'app/components/helpers';
import { filterConstants } from 'app/models/common/enumerators';
import { IColumnLookUpProps } from 'app/models/common/post';
import { IRequestParams } from 'app/models/common/request';
import { usePostRequestType } from 'app/services/mutations';
import { useRequestList, useAssociateRequests } from 'app/services/request-api';
import { CustomerImpactDto, IAssociateRequestsDto, IRequestDto, PaginatedListOfRequestDto, RequestDto, RequestStatus } from 'generated/request-backend';
import useStore, { IStore } from 'app/store';
import { StringConstants } from 'app/utils/constants';
import { getPortalValue } from 'app/utils/utilities';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import '../adminTable/GridTable.scss';
import AssociationSearchBar from './associationSearchBar/AssociationSearchBar';
import './LinkRequestsGridTable.scss';
import { AssociationType } from 'app/enums/AssociationType.enum';

interface IProps {
    requestData: RequestDto[];
    columnsLookUpData: IColumnLookUpProps[];
    gridContRef: React.RefObject<HTMLDivElement>;
    updateModalState: (value: boolean, associateRequest?: boolean) => void;
    selectedRequest: RequestDto;
    setSelectedRequest: Function;
    isAssociatingParent: boolean;
}

const LinkRequestsGridTable: React.FC<IProps> = ({
    requestData,
    columnsLookUpData,
    gridContRef,
    updateModalState,
    selectedRequest,
    isAssociatingParent
}) => {
    const DATA_ITEM_KEY = 'unifiedTrackingId';
    const SELECTED_FIELD = 'selected';
    const idGetter = getter(DATA_ITEM_KEY);
    const pageSize = 50;
    // keeping it less as computing child count for parent requests is expensive operation
    const parentRequestPageSize = 20;

    const [containerWidth, setContainerWidth] = useState(1367);
    const [selectedState, setSelectedState] = useState<{ [id: string]: boolean | number[] }>({});
    const [selectedRequests, setSelectedRequests] = useState<RequestDto[]>([]);
    const [filteredResult, setFilteredResult] = useState<RequestDto[]>([]);

    const history = useHistory();
    const portal = getPortalValue(history?.location?.pathname);

    const [criteria] = useState<IRequestParams>({
        from: '2001-01-01T00:00:00Z',
        to: '9999-12-31T23:59:59Z',
        pageNumber: 1,
        pageSize: isAssociatingParent ? parentRequestPageSize : pageSize,
        type: filterConstants.ALL_REQUEST,
        requestType: [],
        portal: portal,
        hideParentRequests: !isAssociatingParent,
        relationship: isAssociatingParent ? [AssociationType.Parent] as number[] : [AssociationType.None] as number[]
    });

    const requestType = useStore((state: IStore) => state.requestType);

    const { isLoading: isLoadingRequests, refetch: refetchChildList } = useRequestList(criteria, {
        enabled: criteria !== undefined,
        onSuccess: (response: PaginatedListOfRequestDto) => {
            if (isAssociatingParent) {
                setFilteredResult(
                    response.items
                        .filter(
                            (request: RequestDto) =>
                                request.isParent && request.unifiedTrackingId !== selectedRequest.unifiedTrackingId
                        )
                        .map((request: RequestDto) => Object.assign({ selected: false }, request))
                );
            } else {
                setFilteredResult(
                    response.items
                        .filter(
                            (request: RequestDto) =>
                            (request.parentRequestId === null || request.parentRequestId === '') && !request.isParent && request.unifiedTrackingId !== selectedRequest.unifiedTrackingId
                        )
                        .map((request: RequestDto) => Object.assign({ selected: false }, request))
                );
            }

        }
    });

    const { mutate: postRequestTypeHelp, isLoading: isCreatingRequest } = usePostRequestType();
    const { mutate: associateRequests, isLoading: isAssociatingRequests } = useAssociateRequests();

    const customAgingCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return (
            <td>
                <AgingProgress startDate={dataItem.createdOn} endDate={dataItem.requestedResolutionDate} />
            </td>
        );
    };

    const AssignedToCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return (
            <td>
                {dataItem?.assignee ? (
                    <div className="assigned-to-cell">
                        <Avatar
                            userId={dataItem?.assignee?.id}
                            userName={dataItem?.assignee?.displayName}
                            initialColor={PersonaInitialsColor.coolGray}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">{dataItem?.assignee?.displayName}</span>
                    </div>
                ) : (
                    <div className="assigned-to-cell">
                        {' '}
                        <Avatar
                            userId={StringConstants.ZERO}
                            userName={StringConstants.UNASSIGNED}
                            initialColor={PersonaInitialsColor.coolGray}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">Unassigned</span>
                    </div>
                )}
            </td>
        );
    };

    const TitleCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return <td title={dataItem.summary}>{dataItem.summary}</td>;
    };

    const RequestTypeCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return <td title={formatRequestType(dataItem?.requestType)}> {formatRequestType(dataItem?.requestType)}</td>
    }

    const StatusCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return <td title={RequestStatus[dataItem?.status]}> {RequestStatus[dataItem?.status]}</td>
    }

    const TotalRevenueCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        // return <td title={dataItem?.totalRevenue?.toString()}> {formatCurrency(dataItem?.totalRevenue)}</td>;
        return <td title={dataItem?.msxCurrentTotalRevenue?.toString()}> {formatCurrency(dataItem?.msxCurrentTotalRevenue)}</td>; // Should see if we want total revenue or msx revenue here. Also, see why this data item is showing up malformed when searching.
    }

    const setWidth = () => {
        let columnCount = columnsLookUpData.filter((item) => item.isChecked).length;
        if (requestType === StringConstants.MY_REQUEST && columnsLookUpData.filter((item) => item.key === 0 && item.isChecked).length > 0) {
            columnCount = columnCount - 1;
        }
        if (columnCount > 1) {
            let padding = 50 + 47 + 30;
            const availWidth = containerWidth - padding - 90;

            return parseInt((availWidth / columnCount).toString());
        } else {
            return containerWidth;
        }
    };

    const arr = [
        {
            key: 0,
            column: (
                <Column
                    field={SELECTED_FIELD}
                    width={setWidth() - 60}
                    headerSelectionValue={filteredResult?.findIndex((item: RequestDto) => !selectedState[idGetter(item)]) === -1}
                />
            )
        },
        {
            key: 1,
            column: <Column width={setWidth()} className="cell-in-bold" field="unifiedTrackingId" title="ID" resizable={false} />
        },
        {
            key: 2,
            column: <Column width={setWidth()} field="Title" title="TITLE" cell={TitleCell} resizable={false} />
        },
        {
            key: 3,
            column: <Column width={setWidth()} title="REQUEST TYPE" field="requestType" cell={RequestTypeCell} resizable={false} />
        },
        {
            key: 4,
            column: <Column width={setWidth()} field="status" title="STATUS" cell={StatusCell} resizable={false} />
        },
        {
            key: 5,
            column: (
                <Column width={setWidth() + 30} field="assignedTo" title="ASSIGNED TO" cell={AssignedToCell} resizable={false} />
            )
        },
        {
            key: 6,
            column: <Column width={setWidth()} field="createdOn" title="CREATED ON" resizable={false} />
        },
        {
            key: 7,
            column: <Column width={setWidth() - 30} field="aging" title="AGING" cell={customAgingCell} resizable={false} />
        },
        {
            key: 8,
            column: (
                <Column
                    width={setWidth() + 50}
                    field="totalRevenue"
                    title="TOTAL REVENUE RISK"
                    resizable={false}
                    cell={TotalRevenueCell}
                    className="right-aligned-column"
                />
            )
        }
    ];

    const getColumns = () => {
        let list: IColumnLookUpProps[] = [];
        columnsLookUpData.forEach((item) => {
            arr.every((ele) => {
                if (ele.key === item.key && item.isChecked) {
                    list.push(ele);
                    return false;
                } else {
                    return true;
                }
            });
        });

        if (requestType === StringConstants.MY_REQUEST) {
            return list.filter((item: IColumnLookUpProps) => item.key !== 0);
        } else {
            return list;
        }
    };
    const [columnList, setColumnList] = useState(getColumns());

    useEffect(() => {
        if (gridContRef.current?.offsetWidth) {
            setContainerWidth(gridContRef.current.offsetWidth);
        }
    }, [gridContRef.current]);

    useEffect(() => {
        setColumnList(getColumns());
        return () => {};
    }, [columnsLookUpData, requestType]);

    const onSelectionChange = (event: GridSelectionChangeEvent) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey: DATA_ITEM_KEY
        });

        var newSelectedRequests = filteredResult.filter((request: RequestDto) => Object.keys(newSelectedState).includes(request.unifiedTrackingId));
        var requestsToAdd = selectedRequests;

        newSelectedRequests.forEach((request: RequestDto) => {
            var exisitingIds = requestsToAdd.map((item: RequestDto) => item.unifiedTrackingId);
            !exisitingIds.includes(request.unifiedTrackingId) && requestsToAdd.push(request);
        });

        setSelectedRequests(requestsToAdd);
        setSelectedState(newSelectedState);
    };

    const onAssociate = () => {
        var selectedParentRequest = filteredResult.filter((request: RequestDto) => request.unifiedTrackingId === Object.keys(selectedState)[0])[0];

        if (!selectedParentRequest.isParent) {
            let aggregateCustomerImpact = [] as CustomerImpactDto[];

            selectedRequest?.customerImpacts?.forEach((impact: CustomerImpactDto) => {
                aggregateCustomerImpact.push(impact);
            });

            selectedParentRequest?.customerImpacts?.forEach((impact: CustomerImpactDto) => {
                aggregateCustomerImpact.push(impact);
            });

            let datapass: IRequestDto = {
                summary: selectedParentRequest.summary,
                description: selectedParentRequest.description,
                requestedResolutionDate: selectedParentRequest.requestedResolutionDate,
                whyThisDateIsImportant: selectedParentRequest.whyThisDateIsImportant,
                status: selectedParentRequest.status,
                attachments: selectedParentRequest.attachments,
                customerImpacts: aggregateCustomerImpact,
                isParent: true
            };

            const requestAggregateData = requestAggregateSubmit(
                selectedParentRequest.requestType,
                selectedParentRequest,
                datapass
            );

            postRequestTypeHelp(
                { subUrl: requestAggregateData.subUrl, data: requestAggregateData.data },
                {
                    onSuccess: (response) => {
                        finishParentAssociation(response, selectedParentRequest);
                    }
                }
            );
        } else {
            const associationObject : IAssociateRequestsDto = {
                requestIds: [selectedRequest.unifiedTrackingId],
                associatedRequestId: selectedParentRequest.unifiedTrackingId,
                expectedCustomerFamilySubmissionId: selectedParentRequest.customerFamilySubmissionId,
                expectedCustomerFamilyGeneration: selectedParentRequest.customerFamilyGeneration
            };

            associateRequests(associationObject, {
                onSuccess: () => {
                    updateModalState(false, true);
                }
            });
        }
    };

    const finishParentAssociation = async (parentId: string, selectedParentRequest: RequestDto) => {
        const associateRequestsObject: IAssociateRequestsDto = {
            requestIds: [selectedRequest.unifiedTrackingId, selectedParentRequest?.unifiedTrackingId],
            associatedRequestId: parentId,
            expectedCustomerFamilySubmissionId: selectedParentRequest.customerFamilySubmissionId,
            expectedCustomerFamilyGeneration: selectedParentRequest.customerFamilyGeneration
        };

        associateRequests(associateRequestsObject, {
            onSuccess: () => {
                updateModalState(false, true);
            }
        });
    };

    const onAssociateChildren = () => {
        var selectedRequestKeys = Object.keys(selectedState).filter((key) => selectedState[key] === true);
        var associateRequestsObject: IAssociateRequestsDto = {
            requestIds: selectedRequestKeys,
            associatedRequestId: selectedRequest.isParent ? selectedRequest.unifiedTrackingId : selectedRequest.parentRequestId,
            expectedCustomerFamilySubmissionId: selectedRequest.customerFamilySubmissionId,
            expectedCustomerFamilyGeneration: selectedRequest.customerFamilyGeneration
        };

        associateRequests(associateRequestsObject, {
            onSuccess: () => {
                updateModalState(false, true);
            }
        });
    };

    if (requestData) {
        return (
            <div className="table-content">
                {isCreatingRequest || isAssociatingRequests ? (
                    <div>
                        <div className="m-t-50">
                            <Spinner label="Associating Request. Please wait..." size={SpinnerSize.large} />
                        </div>
                    </div>
                ) : (
                    <div className="grid-cont" role="main" aria-label="grid table">
                        <div className="lr-header">
                            <div className="head-col-left">
                                <Text className="dp-head-title">
                                    {isAssociatingParent
                                        ? 'Choose from exisiting requests'
                                        : `${
                                              Object.values(selectedState).filter((value) => value === true).length
                                          } Requests selected`}
                                </Text>
                            </div>
                            <div className="head-col-right">
                                <AssociationSearchBar
                                    isAssociatingParent={isAssociatingParent}
                                    setFilteredResult={setFilteredResult}
                                    refetchChildList={refetchChildList}
                                />
                            </div>
                        </div>
                        <Tooltip openDelay={100} position="right" anchorElement="element">
                            {isLoadingRequests ? (
                                <div>
                                    <div className="m-t-50">
                                        <Spinner label="Loading requests..." size={SpinnerSize.large} />
                                    </div>
                                </div>
                            ) : (
                                <Grid
                                    data={selectedRequests
                                        ?.map((request: RequestDto) => ({
                                            ...request,
                                            [SELECTED_FIELD]: selectedState[idGetter(request)]
                                        }))
                                        .filter((request: IRequestDto) => request[SELECTED_FIELD as keyof IRequestDto] === true)
                                        .concat(
                                            filteredResult
                                                ?.map((request: RequestDto) => ({
                                                    ...request,
                                                    [SELECTED_FIELD]: selectedState[idGetter(request)]
                                                }))
                                                .filter(
                                                    (request) =>
                                                        !Object.keys(selectedState).includes(request.unifiedTrackingId) ||
                                                        request[SELECTED_FIELD] === false
                                                )
                                        )}
                                    dataItemKey={DATA_ITEM_KEY}
                                    sortable={true}
                                    onSelectionChange={onSelectionChange}
                                    resizable={false}
                                    selectedField={SELECTED_FIELD}
                                    selectable={{ enabled: true, cell: false, mode: isAssociatingParent ? 'single' : 'multiple' }}
                                    navigatable={true}>
                                    {columnList?.map((item: IColumnLookUpProps) => item.column)}
                                </Grid>
                            )}
                        </Tooltip>
                        {isAssociatingParent ? (
                            selectedState[Object.keys(selectedState)[0]] ? (
                                <div className="parent-table-btn-wrap">
                                    <DefaultButton className="default" onClick={() => updateModalState(false)}>
                                        <Trans>Cancel</Trans>
                                    </DefaultButton>
                                    <DefaultButton type="submit" className="primary" onClick={onAssociate}>
                                        <Trans>Associate as parent</Trans>
                                    </DefaultButton>
                                </div>
                            ) : (
                                <></>
                            )
                        ) : (
                            <div className="parent-table-btn-wrap">
                                <DefaultButton className="default" onClick={() => updateModalState(false)}>
                                    <Trans>Cancel</Trans>
                                </DefaultButton>
                                <DefaultButton
                                    type="submit"
                                    className="primary"
                                    onClick={onAssociateChildren}
                                    disabled={Object.values(selectedState).filter((value) => value === true).length <= 0}>
                                    <Trans>Associate as child</Trans>
                                </DefaultButton>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    } else {
        <div>Loading...</div>;
    }
};

export default LinkRequestsGridTable;
